import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Cell, ConceptRow } from '@balance/domain';
import { CellDetail } from '../components/CellDetail.js';
import { NewsNotice, useNews } from '../components/News.js';
import { Badge, ConceptName, Meter, StatusDot, cellClass } from '../components/ui.js';
import { eur, monthName } from '../format.js';
import { useApp } from '../state.js';

export function YearStrip() {
  const { balance, period, setPeriod } = useApp();
  if (!balance) return null;
  return (
    <div className="year" role="tablist" aria-label="Meses">
      {balance.periods.map((p) => {
        const t = balance.totals[p.period]!;
        const cls = p.status === 'future' ? 'future' : t.resultCents >= 0 ? 'pos' : 'neg';
        return (
          <button type="button" key={p.period} role="tab" aria-selected={p.period === period} className={`${cls}${p.period === period ? ' selected' : ''}`} onClick={() => setPeriod(p.period)}>
            {p.shortLabel}
            <b>{eur(t.resultCents, { sign: true })}</b>
            <span className="tiny">{p.status === 'closed' ? 'Cerrado' : p.status === 'current' ? 'En curso' : 'Previsto'}</span>
          </button>
        );
      })}
    </div>
  );
}

export function MonthlyPage() {
  const { balance, period } = useApp();
  const news = useNews();
  const [detail, setDetail] = useState<{ row: ConceptRow; cell: Cell } | null>(null);
  const groups = useMemo(() => {
    if (!balance) return [];
    const out: Array<{ key: string; name: string; rows: ConceptRow[] }> = [];
    for (const row of balance.rows) {
      const cell = row.cells[period];
      if (!cell) continue;
      if (cell.status === 'inactive' && cell.actualCents === 0) continue;
      const key = row.direction === 'income' ? 'income' : row.categoryId ?? 'none';
      const name = row.direction === 'income' ? 'Ingresos' : row.categoryName;
      let g = out.find((x) => x.key === key);
      if (!g) {
        g = { key, name, rows: [] };
        out.push(g);
      }
      g.rows.push(row);
    }
    return out;
  }, [balance, period]);
  if (!balance || !period) return <div className="skeleton" />;
  const totals = balance.totals[period]!;
  const info = balance.periods.find((p) => p.period === period)!;
  const meters = balance.meters[period] ?? [];
  const openReceivables = balance.receivables.filter((r) => !r.closedOn);
  const toReview = balance.pending.length + balance.receivables.filter((r) => r.overdue).length + meters.filter((m) => m.overCents > 0).length;
  const anim = (conceptId: string, value: number) => {
    if (!news.playing) return value;
    const c = news.playing.changes.find((x) => x.kind === 'meter' && x.conceptId === conceptId && x.period === period);
    return c ? Math.round(c.before + (c.after - c.before) * news.playing.progress) : value;
  };
  const animTotal = (label: string, value: number) => {
    if (!news.playing) return value;
    const c = news.playing.changes.find((x) => x.kind === 'total' && x.label === `${label} · ${period}`);
    return c ? Math.round(c.before + (c.after - c.before) * news.playing.progress) : value;
  };
  const expense = animTotal('Egresos', totals.expenseCents);
  const income = animTotal('Ingresos', totals.incomeCents);

  return (
    <div className="page">
      <NewsNotice news={news} />
      <YearStrip />
      <div className="stats">
        <div className="stat">
          <small>Ingresos</small>
          <strong>{eur(income)}</strong>
          <small>{totals.actualIncomeCents ? `${eur(totals.actualIncomeCents)} ya cobrados` : 'Ingreso familiar del mes'}</small>
        </div>
        <div className="stat">
          <small>{info.status === 'closed' ? 'Egresos' : 'Egresos previstos'}</small>
          <strong>{eur(expense)}</strong>
          <small>{eur(totals.actualExpenseCents)} reales imputados{totals.pendingOutCents ? ` · ${eur(totals.pendingOutCents)} sin clasificar` : ''}</small>
        </div>
        <div className="stat hero">
          <small>{info.status === 'closed' ? 'Resto' : 'Resto previsto'}</small>
          <strong>{eur(income - expense)}</strong>
          <small>{totals.pendingOutCents || totals.pendingInCents ? `Provisional con pendientes: ${eur(totals.provisionalResultCents)}` : `Al completar ${monthName(period)}`}</small>
        </div>
        <div className="stat">
          <small>Acumulado al cierre</small>
          <strong>{eur(totals.accumulatedCents)}</strong>
          <small>{totals.financingCents ? `Incluye ${eur(totals.financingCents)} recibidos de un préstamo (no es ahorro)` : `Desde ${eur(balance.openingBalanceCents)} al inicio de ${monthName(balance.openingPeriod)}`}</small>
        </div>
      </div>
      <div className="grid">
        <div>
          <div className="panel">
            <div className="panelhead">
              <h2>El detalle del mes</h2>
              <Link className="button" to="/grilla">
                Ver en grilla
              </Link>
            </div>
            {balance.rows.length === 0 ? (
              <div className="empty" style={{ textAlign: 'left', padding: '8px 0 16px' }}>
                <p style={{ color: 'var(--ink)' }}>Todavía no hay conceptos. Para que el mes tenga sentido, empezá por:</p>
                <ol className="muted" style={{ paddingLeft: 18, margin: '0 0 16px' }}>
                  <li>los sueldos (Ingresos);</li>
                  <li>los gastos fijos y suscripciones: alquiler, servicios, psicólogas…;</li>
                  <li>los presupuestos que se consumen: supermercado, fin de semana, ropa.</li>
                </ol>
                <div className="inline">
                  <Link className="button primary" to="/conceptos/ingresos">
                    + Cargar sueldos
                  </Link>
                  <Link className="button" to="/conceptos">
                    + Crear un concepto
                  </Link>
                </div>
              </div>
            ) : null}
            {groups.map((g) => (
              <div key={g.key}>
                <div className="row group">
                  <div className="eyebrow">{g.name}</div>
                </div>
                {g.rows.map((row) => {
                  const cell = row.cells[period]!;
                  return (
                    <div className="row" key={row.conceptId}>
                      <div>
                        <StatusDot status={cell.status} />
                        <ConceptName id={row.conceptId} name={row.name} />
                        {cell.status === 'over' ? <Badge kind="red">exceso {eur(cell.overCents)}</Badge> : null}
                        {cell.overridden ? <span className="badge warn" style={{ marginLeft: 6 }}>ajuste</span> : null}
                        {cell.cancelled.length ? <span className="muted"> · {cell.cancelled.length} cancelada{cell.cancelled.length > 1 ? 's' : ''}</span> : null}
                      </div>
                      <button type="button" className={`linkbutton number ${cellClass(cell)}`} style={{ color: cell.status === 'over' ? 'var(--red)' : cell.status === 'none' ? '#a9b1ab' : 'inherit' }} onClick={() => setDetail({ row, cell })} title="Ver qué compone esta cifra">
                        {eur(cell.projectedCents)}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
            <div className="row" style={{ borderTop: '1px solid var(--line)', marginTop: 8 }}>
              <strong>Egresos</strong>
              <strong className="number">{eur(totals.expenseCents)}</strong>
            </div>
            {totals.pendingOutCents ? (
              <div className="row dim">
                <div>
                  Sin clasificar <Link to="/revisar">resolver</Link>
                </div>
                <span className="number">{eur(totals.pendingOutCents)}</span>
              </div>
            ) : null}
          </div>
        </div>
        <div>
          <div className="panel">
            <h2 style={{ marginBottom: 22 }}>Presupuestos de consumo</h2>
            {meters.length === 0 ? (
              <p className="muted">
                Ningún presupuesto con medidor este mes. <Link to="/conceptos/presupuestos">Crear un presupuesto</Link>
              </p>
            ) : null}
            {meters.map((m) => (
              <Meter key={m.conceptId} conceptId={m.conceptId} name={m.name} spent={anim(m.conceptId, m.spentCents)} budget={m.budgetCents} over={Math.max(0, anim(m.conceptId, m.spentCents) - m.budgetCents)} remaining={Math.max(0, m.budgetCents - anim(m.conceptId, m.spentCents))} />
            ))}
            <Link className="button" to="/fines-de-semana">
              Ver períodos de ocio
            </Link>
          </div>
          <MonthDifferences />
          <LoansPanel />
          <div className="panel">
            <h2>Por recuperar</h2>
            {openReceivables.length === 0 ? <p className="muted">No hay devoluciones pendientes.</p> : null}
            {openReceivables.map((r) => (
              <div className="row" key={r.receivableId}>
                <div>
                  {r.description}
                  <div className="muted">
                    {r.period} · vence {r.dueOn} {r.overdue ? <Badge kind="warn">vencido</Badge> : null}
                  </div>
                </div>
                <span className="number">{eur(r.pendingCents)}</span>
              </div>
            ))}
            <Link className="button" to="/por-recuperar" style={{ marginTop: 8 }}>
              Ver cuentas corrientes
            </Link>
          </div>
          <div className="panel">
            <h2>Por revisar</h2>
            <div className="row">
              <div>Clasificación, período o exceso</div>
              <Badge kind={toReview ? 'warn' : ''}>{toReview}</Badge>
            </div>
            <Link className="button primary" to="/revisar" style={{ marginTop: 8 }}>
              Resolver pendientes
            </Link>
          </div>
        </div>
      </div>
      {detail ? <CellDetail row={detail.row} cell={detail.cell} onClose={() => setDetail(null)} /> : null}
    </div>
  );
}

/**
 * Diferencias contra lo planificado. Mes cerrado: resultado del cierre (lo no gastado vuelve al ahorro,
 * lo excedido lo reduce). Mes en curso: qué quedaría de los presupuestos si cerrara hoy. Las cifras
 * vienen del motor; aquí sólo se presentan.
 */
function MonthDifferences() {
  const { balance, period } = useApp();
  if (!balance || !period) return null;
  const info = balance.periods.find((p) => p.period === period);
  const totals = balance.totals[period];
  if (!info || !totals || info.status === 'future') return null;
  const closed = info.status === 'closed';
  const items = balance.rows
    .map((row) => ({ row, cell: row.cells[period]! }))
    .filter(({ cell }) => (closed ? cell.varianceCents !== null && cell.varianceCents !== 0 : cell.remainingIfClosedCents !== null))
    .map(({ row, cell }) => ({ row, cell, value: closed ? cell.varianceCents! : cell.remainingIfClosedCents! }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  const total = closed ? totals.varianceCents : totals.budgetRemainingIfClosedCents;
  if (items.length === 0 && total === 0) return null;
  return (
    <div className="panel">
      <div className="panelhead" style={{ marginBottom: 8 }}>
        <h2>{closed ? `Cierre de ${monthName(period)}` : 'Si el mes cerrara hoy'}</h2>
        <Badge kind={total >= 0 ? '' : 'red'}>{total >= 0 ? `+${eur(total).replace('−', '')} al ahorro` : `${eur(total)} en contra`}</Badge>
      </div>
      <p className="muted">
        {closed
          ? 'Diferencias entre lo planificado y lo real: lo que no se gastó ya volvió al acumulado; lo que se pasó lo redujo.'
          : 'Mientras el mes está abierto la previsión da por gastado lo presupuestado. Esto es lo que sobraría (o faltaría) de los presupuestos si no se gastara nada más.'}
      </p>
      {items.length === 0 ? <p className="muted">Sin diferencias.</p> : null}
      {items.slice(0, 8).map(({ row, value }) => (
        <div className="row" key={row.conceptId}>
          <ConceptName id={row.conceptId} name={row.name} />
          <span className={`number ${value >= 0 ? 'positive' : 'negative'}`}>{eur(value, { sign: true })}</span>
        </div>
      ))}
      {items.length > 8 ? <div className="muted">y {items.length - 8} más en la grilla</div> : null}
    </div>
  );
}

/** Préstamos recibidos: importe que entró, cuotas descontadas y saldo de seguimiento al cierre del mes. */
function LoansPanel() {
  const { balance, period } = useApp();
  if (!balance || !period || balance.loans.length === 0) return null;
  return (
    <div className="panel">
      <h2>Préstamos</h2>
      {balance.loans.map((loan) => {
        const p = loan.perPeriod[period];
        if (!p) return null;
        return (
          <div key={loan.conceptId} style={{ marginTop: 12 }}>
            <div className="panelhead" style={{ marginBottom: 4 }}>
              <h3>
                <ConceptName id={loan.conceptId} name={loan.name} />
              </h3>
              <span className="big">{eur(Math.max(0, p.trackingRemainingCents))}</span>
            </div>
            <div className="muted">
              Recibido {eur(loan.principalCents)} el {loan.receivedOn} · {p.instalmentsPaid}
              {loan.instalmentsTotal ? ` de ${loan.instalmentsTotal}` : ''} cuotas ({eur(p.repaidCents)}) a fin de {monthName(period)}
            </div>
          </div>
        );
      })}
      <p className="muted" style={{ marginTop: 12 }}>
        Saldo de seguimiento = recibido − cuotas. Lo recibido sumó al acumulado y cada cuota lo descuenta. No es el capital del contrato: si las cuotas incluyen intereses, lo pendiente real puede ser distinto.
      </p>
    </div>
  );
}
